import { useMemo, useEffect } from 'react';
import { useUserContext } from './userContext';
import { useGetCourseEnrollmentStatus } from './use-get-course-enrollment-status';
import { useQuery } from '@apollo/client';
import { GET_HOMEOWNER_STEP_STATUS } from '../graphql/homeowner-matching-profile';
import { GET_HOMEOWNER_BIO } from '../graphql/homeowner-bio';
import { GET_HOMEOWNER_PROJECTS } from '../graphql/homeowner-project';

export interface HomeownerOnboardingAccess {
  courseCompleted: boolean;
  matchingProfileCompleted: boolean;
  matchingProfileUnlocked: boolean;
  projectCompleted: boolean;
  bioCompleted: boolean;
  bioUnlocked: boolean;
  isOnboardingComplete: boolean;
}

export const useHomeownerOnboardingAccess = (): HomeownerOnboardingAccess => {
  const { user } = useUserContext();
  const { getCourseEnrollmentStatus, data: courseData } = useGetCourseEnrollmentStatus();
  
  // Fetch course enrollment status when user is available
  useEffect(() => {
    if (user?.id && (user?.accountType?.toLowerCase() || "") === "homeowner") {
      getCourseEnrollmentStatus({
        variables: { userId: user.id }
      });
    }
  }, [user?.id, user?.accountType, getCourseEnrollmentStatus]);
  
  // Get homeowner matching profile status
  const { data: homeownerStepStatusData } = useQuery(GET_HOMEOWNER_STEP_STATUS, {
    variables: { homeOwnerId: user?.id || "" },
    skip: !user?.id || (user?.accountType?.toLowerCase() || "") !== "homeowner",
    fetchPolicy: 'cache-and-network',
  });
  const { data: homeownerBioData } = useQuery(GET_HOMEOWNER_BIO, {
    variables: { homeOwnerId: user?.id || "" },
    skip: !user?.id || (user?.accountType?.toLowerCase() || "") !== "homeowner",
    fetchPolicy: "cache-and-network",
  });
  const { data: homeownerProjectsData } = useQuery(GET_HOMEOWNER_PROJECTS, {
    variables: { homeOwnerId: user?.id || "" },
    skip: !user?.id || (user?.accountType?.toLowerCase() || "") !== "homeowner",
    fetchPolicy: "cache-and-network",
  });

  return useMemo(() => {
    const isHomeowner = (user?.accountType?.toLowerCase() || "") === "homeowner";
    
    if (!isHomeowner) {
      return {
        courseCompleted: false,
        matchingProfileCompleted: false,
        matchingProfileUnlocked: false,
        projectCompleted: false,
        bioCompleted: false,
        bioUnlocked: false,
        isOnboardingComplete: false,
      };
    }

    // Check course completion
    const courseCompleted =
      courseData?.getCourseEnrollmentStatus?.status === "completed" ||
      (user as any)?.courseEnrollment?.status === "completed" ||
      (user as any)?.applicationStatus === "course_completed";

    // Check matching profile completion - personality, style, project, preferences, priorities
    const stepStatus = homeownerStepStatusData?.getHomeownerStepStatus;
    const stepCompletion = stepStatus?.profile?.stepCompletion;
    const completedSteps = stepStatus?.completedSteps ?? [];
    const matchingProfileCompletedBySteps = completedSteps.includes("priorities");
    const matchingProfileCompleted =
      (stepCompletion
        ? (
            stepCompletion.personalityCompleted &&
            stepCompletion.styleCompleted &&
            stepCompletion.projectCompleted &&
            stepCompletion.preferenceCompleted &&
            stepCompletion.prioritiesCompleted
          )
        : false) || matchingProfileCompletedBySteps;

    // Matching profile unlocks after course is completed
    const matchingProfileUnlocked = courseCompleted;

    const projectCompleted = Boolean(homeownerProjectsData?.getHomeOwnerProjects?.length);

    const bioCompleted = Boolean(homeownerBioData?.getHomeOwnerBio?._id);

    // Bio unlocks after matching profile is completed
    const bioUnlocked = matchingProfileCompleted;

    // Onboarding is complete when all steps are done (course, matching profile, first project)
    const isOnboardingComplete =
      courseCompleted && matchingProfileCompleted && projectCompleted;

    console.log('🔍 HomeownerOnboardingAccess:', {
      courseCompleted,
      matchingProfileCompleted,
      matchingProfileUnlocked,
      projectCompleted,
      bioCompleted,
      bioUnlocked,
      isOnboardingComplete,
      stepCompletion,
    });

    return {
      courseCompleted,
      matchingProfileCompleted,
      matchingProfileUnlocked,
      projectCompleted,
      bioCompleted,
      bioUnlocked,
      isOnboardingComplete,
    };
  }, [user, courseData, homeownerStepStatusData, homeownerBioData, homeownerProjectsData]);
};
